import React, { useContext, useState } from 'react';
import { Box, Container, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { ApiContext } from '../../contexts/ApiContext';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import FixedLabelTextField from '../../components/FixedLabelTextField';
import { useOrganizationKey } from '../../hooks/useOrganizationKey';
import { organizationSelector } from '../../state/organizations/selectors';
import { addOrUpdateOrganization } from '../../state/organizations/actions';
import LoadingButton from '@mui/lab/LoadingButton';
import AvatarPicker from '../../components/AvatarPicker';
import { useAvatarPicture } from '../../hooks/useAvatarPicture';
import { getLargePictureVariant } from '../../utils/avatarUtils';
import { toId } from '../../utils/apiUtils';

const StyledContainer = styled(Container, { name: 'GeneralSettings' })(
  ({ theme, color }) => ({}),
);

const GeneralOrganizationSettingsView = ({}) => {
  const { mutationApi, storageApi } = useContext(ApiContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const organizationKey = useOrganizationKey();
  const organization = useSelector(organizationSelector(organizationKey));
  const [name, setName] = React.useState(organization.name);
  const [nameError, setNameError] = useState();
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPictureFile, setSelectedPictureFile] = useState();
  const [pictureUrl, setPictureUrl] = useAvatarPicture(
    getLargePictureVariant(organization?.picture),
  );
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);

  const handleOnAvatarSelected = (file) => {
    // Release previously created object URL if any
    if (selectedPictureFile) {
      URL.revokeObjectURL(pictureUrl);
    }
    setPictureUrl(URL.createObjectURL(file));
    setSelectedPictureFile(file);
    setChanged(true);
  };

  const handleAvatarCleared = () => {
    // Release previously created object URL if any
    if (selectedPictureFile) {
      URL.revokeObjectURL(pictureUrl);
    }
    setPictureUrl(null);
    setSelectedPictureFile(null);
    setChanged(true);
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    setNameError(value.length === 0 ? t('nameIsRequired') : null);
    setName(value);
    setChanged(true);
  };

  const handleSaveButtonClick = async () => {
    const data = {};
    if (name !== organization.name) {
      data.name = name;
    }

    // Remove team's original avatar if user clicked on Remove button
    if (!pictureUrl && organization.picture) {
      await storageApi.deleteAvatarPictures(organization.picture);
      data.picture = null;
    }

    if (selectedPictureFile) {
      // Remove user's original avatar if another image was selected
      if (organization.picture) {
        await storageApi.deleteAvatarPictures(organization.picture);
      }

      setUploading(true);
      const key = await storageApi.uploadWorkspacePicture(
        selectedPictureFile,
        toId(organizationKey),
        setUploadProgress,
      );
      data.picture = key;
      setUploading(false);
    }

    // Data can have zero keys, when for example name is changed and then changed
    // back to the original value. The value won't be added to the data object but
    // changed state will be true, which caused the save button to be enabled
    if (Object.keys(data) === 0) {
      return;
    }

    setSaving(true);
    dispatch(addOrUpdateOrganization(organizationKey, data));
    // TODO: Move to MutationAPI class
    await mutationApi.updatePrivateResource(
      organizationKey,
      organizationKey,
      data,
    );
    data.picture = undefined; // organization public resource don't store images
    await mutationApi.updatePublicResource(
      organizationKey,
      organizationKey,
      data,
    );
    setChanged(false);
    setSaving(false);
  };

  return (
    <Stack spacing={4}>
      <Stack direction="row" spacing={4} alignItems="center">
        <AvatarPicker
          name={name}
          imageUrl={pictureUrl}
          variant="circular"
          isUploading={uploading}
          uploadProgress={uploadProgress}
          onFileSelected={handleOnAvatarSelected}
          onClear={handleAvatarCleared}
        />
        <Box flex="1 1 auto">
          <FixedLabelTextField
            required
            fullWidth
            label={t('name')}
            variant="outlined"
            value={name || ''}
            error={Boolean(nameError)}
            helperText={nameError}
            onChange={handleNameChange}
          />
        </Box>
      </Stack>
      <Box sx={{ '&&': { ml: 'auto' } }}>
        <LoadingButton
          autoFocus
          color="primary"
          variant="contained"
          size="large"
          loading={saving}
          disabled={!changed || nameError || uploading}
          onClick={handleSaveButtonClick}
        >
          {t('Save changes')}
        </LoadingButton>
      </Box>
    </Stack>
  );
};

export default GeneralOrganizationSettingsView;
